import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchPolicyById } from "../policies/policySlice";
import { calculateCommission } from "../../CommissionInfo/commissionLogic";

// MUI
import Box from "@mui/material/Box";
import { firebaseCache } from "../../utils/firebaseCache";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import SouthWestIcon from "@mui/icons-material/SouthWest";
import PaymentIcon from "@mui/icons-material/Payment";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import DescriptionIcon from "@mui/icons-material/Description";
import BusinessIcon from "@mui/icons-material/Business";
import TagIcon from "@mui/icons-material/LocalOffer";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ListAltIcon from "@mui/icons-material/ListAlt";
import FlagIcon from "@mui/icons-material/Flag";
import HandymanIcon from "@mui/icons-material/Handyman";
import NumbersIcon from "@mui/icons-material/Numbers";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PercentIcon from "@mui/icons-material/Percent";
import CommissionEditDialog from "./CommissionEditDialog";
import { toast } from "react-toastify";

import { getVehicleTypeDisplayName } from "../../utils/vehicleTypeMapper";
import StatusBadge from "../../components/common/StatusBadge";
import { calculatePolicyStatus } from "../../utils/dateUtils";
import { shouldUseMobileLayout } from "../../utils/deviceDetection";

const CommissionDetail = () => {
    const dispatch = useDispatch();
    const { id: policyId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.auth);
    const isMobile = shouldUseMobileLayout();

    // Get the page and category we came from
    const fromPage = location.state?.fromPage || location.state?.currentPage || 1;
    const fromCategory = location.state?.fromCategory || 'motor';

    const [policy, setPolicy] = useState(location.state?.policy || null);
    const [loading, setLoading] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);

    // Derive userId consistently
    const userId = location.state?.policy?.agentId || user?.uid || location.state?.userId;

    const fetchPolicy = async () => {
        try {
            setLoading(true);
            setLoading(true);
            if (!userId || !policyId) {
                console.error("❌ Missing userId or policyId");
                return;
            }

            // Use Redux action for optimized fetching with caching
            const result = await dispatch(fetchPolicyById({ agentId: userId, id: policyId }));
            const fetchedPolicy = result.payload;

            if (fetchedPolicy) {
                setPolicy(fetchedPolicy);
            } else {
                console.warn("❌ Policy not found.");
            }
        } catch (err) {
            console.error("🔥 Error fetching policy:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const effectiveUserId = location.state?.policy?.agentId || user?.uid || location.state?.userId;
        if (policyId && effectiveUserId) {
            fetchPolicy();
        }
    }, [policyId, user?.uid, location.state]);

    if (!policy && loading) {
        return (
            <Box sx={{ px: 2, pt: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <Typography variant="h6" sx={{ color: '#757575' }}>Loading commission details...</Typography>
            </Box>
        );
    }

    if (!policy && !loading) {
        return (
            <Box sx={{ px: 2, pt: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <Typography variant="h6" sx={{ color: '#757575' }}>Policy not found.</Typography>
            </Box>
        );
    }

    const commissionRate = policy.commissionRate || 0;
    const tdsRate = policy.tdsRate || 2;

    const netPremium = parseFloat(policy.netPremium) || 0;
    const discountAmount = parseFloat(policy.discount) || 0;
    const amountReceived = parseFloat(policy.paidAmount) || 0;

    let baseCommission, totalCommission;

    if (policy.policyCategory === 'motor') {
        const odCommissionAmount = parseFloat(policy.odCommissionAmount) || 0;
        const tpCommissionAmount = parseFloat(policy.tpCommissionAmount) || 0;
        baseCommission = odCommissionAmount + tpCommissionAmount;
    } else {
        if (commissionRate === 0) {
            baseCommission = 0;
        } else {
            baseCommission = (netPremium * (commissionRate / 100));
        }
    }

    // Total Commission = Gross Commission * (1 - TDS%)
    // Total Commission: Use stored value if available, else calculate
    const calculatedCommission = baseCommission * (1 - (tdsRate / 100));
    totalCommission = policy.totalCommission !== undefined && policy.totalCommission !== null
        ? Math.max(0, parseFloat(policy.totalCommission))
        : Math.max(0, calculatedCommission);

    // If we have a total commission but baseCommission is 0 (e.g. from bulk upload),
    // derive baseCommission for UI consistency
    if (totalCommission > 0 && baseCommission === 0) {
        baseCommission = totalCommission / (1 - (tdsRate / 100));
    }

    // Compute simple status from expiry (fallback to provided status)
    const computeStatus = () => {
        return calculatePolicyStatus(policy.expiryDate, policy.status) || "-";
    };

    const statusText = computeStatus();

    return (
        <Box sx={{ px: 2, pt: 1, pb: 10, minHeight: 'fit-content', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <IconButton
                    onClick={() => {
                        const target = location.state?.from || '/commission';
                        navigate(target, { state: { currentPage: fromPage, fromCategory: fromCategory } });
                    }}
                    size="small"
                    sx={{
                        "&:hover": {
                            backgroundColor: "rgba(0,0,0,0.04)",
                        },
                    }}
                >
                    <ArrowBackIcon sx={{ color: "#212121" }} />
                </IconButton>
                <Typography variant="h5" sx={{
                    fontFamily: 'Roboto Slab',
                    fontWeight: 550,
                    fontSize: '32px',
                    lineHeight: '56px'
                }}>Commission Details</Typography>
            </Box>

            {/* Client card - left aligned, no buttons */}
            <Paper
                sx={{
                    width: '100%',
                    height: 100,
                    gap: '16px',
                    px: { xs: 2, sm: 4 },
                    py: { xs: 2, sm: 3 },
                    boxShadow: 'none',
                    backgroundColor: '#0063F7',
                    color: '#FFFFFF',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    mb: 0,
                }}
            >
                <Avatar sx={{ width: 48, height: 48, bgcolor: '#E0EBFF', color: '#0063F7', mr: 2 }}>
                    <PersonIcon sx={{ fontSize: 28 }} />
                </Avatar>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', flex: 1 }}>
                    <Typography sx={{
                        fontFamily: 'Roboto',
                        fontWeight: 500,
                        fontSize: '18px',
                        lineHeight: '28px',
                        letterSpacing: '0.01em',
                        opacity: 1,
                        textAlign: 'left',
                        mb: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '24px'
                    }}>
                        {policy.name || '-'}
                        {policy.referredBy && (
                            <Typography component="span" sx={{
                                fontFamily: 'Roboto',
                                fontWeight: 500,
                                fontSize: '18px',
                                lineHeight: '28px',
                                letterSpacing: '0.01em',
                                opacity: 0.9,
                                ml: 2
                            }}>
                                Referred By: {policy.referredBy}
                            </Typography>
                        )}
                    </Typography>
                    <Typography sx={{
                        fontFamily: 'Roboto',
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '20px',
                        letterSpacing: '0.01em',
                        color: '#FFFFFF',
                        opacity: 1,
                        textAlign: 'left'
                    }}>{policy.mobile || '-'}</Typography>
                </Box>
            </Paper>

            {/* Main content container */}
            <Box
                sx={{
                    width: '100%',
                    background: '#fff',
                    borderRadius: { xs: '0 0 12px 12px', sm: '0 0 16px 16px' },
                    boxShadow: 'none',
                    px: { xs: 2, sm: 3 },
                    pt: 2,
                    pb: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    gap: 0,
                    flexShrink: 0
                }}
            >
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2, px: 0, py: 2 }}>
                    {policy?.policyCategory === 'motor' && (
                        <Box>
                            <Box sx={{ pt: 1, pb: 0.5, pl: 2 }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontSize: '18px',
                                        fontWeight: 600,
                                    }}
                                >
                                    Vehicle Details
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    background: "#fff",
                                    borderTop: '1px solid #E5E7EB',
                                    padding: "12px 20px",
                                    display: "grid",
                                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
                                    rowGap: "16px",
                                    columnGap: "16px",
                                    alignItems: "start",
                                    boxShadow: 'none',
                                }}
                            >
                                <Box sx={{
                                    display: "grid",
                                    gridTemplateRows: "auto auto",
                                    alignItems: "start",
                                    flexGrow: 1,
                                }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <DirectionsCarIcon sx={{ fontSize: { xs: 12, sm: 13, md: 14 }, color: "#626B77" }} />
                                        <Typography
                                            sx={{
                                                fontSize: "14px",
                                                fontWeight: 400,
                                                color: "#626B77",
                                            }}
                                        >
                                            Vehicle Type
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "flex-start", ml: 2.25 }}>
                                        <Typography
                                            sx={{
                                                fontSize: "18px !important",
                                                fontWeight: 500,
                                                color: "#000",
                                            }}
                                        >
                                            {getVehicleTypeDisplayName(policy.vehicleType)}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={{
                                    display: "grid",
                                    gridTemplateRows: "auto auto",
                                    alignItems: "start",
                                    flexGrow: 1,
                                }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <NumbersIcon sx={{ fontSize: { xs: 12, sm: 13, md: 14 }, color: "#626B77" }} />
                                        <Typography
                                            sx={{
                                                fontSize: "14px",
                                                fontWeight: 400,
                                                color: "#626B77",
                                            }}
                                        >
                                            Vehicle Number
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "flex-start", ml: 2.25 }}>
                                        <Typography
                                            sx={{
                                                fontSize: "18px !important",
                                                fontWeight: 500,
                                                color: "#000",
                                            }}
                                        >
                                            {policy.vehicleNumber || '-'}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={{
                                    display: "grid",
                                    gridTemplateRows: "auto auto",
                                    alignItems: "start",
                                    flexGrow: 1,
                                }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <HandymanIcon sx={{ fontSize: { xs: 12, sm: 13, md: 14 }, color: "#626B77" }} />
                                        <Typography
                                            sx={{
                                                fontSize: "14px",
                                                fontWeight: 400,
                                                color: "#626B77",
                                            }}
                                        >
                                            Make & Model
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "flex-start", ml: 2.25 }}>
                                        <Typography
                                            sx={{
                                                fontSize: "18px !important",
                                                fontWeight: 500,
                                                color: "#000",
                                            }}
                                        >
                                            {policy.vehicleMake ? `${policy.vehicleMake} ${policy.vehicleModel || ''}`.trim() : (policy.vehicleModel || '-')}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    <Box>
                        <Box sx={{ pt: 1, pb: 0.5, pl: 2 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontSize: '18px',
                                    fontWeight: 600,
                                }}
                            >
                                Policy Details
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                background: "#fff",
                                borderTop: '1px solid #E5E7EB',
                                padding: "12px 20px",
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
                                rowGap: "16px",
                                columnGap: "16px",
                                alignItems: "start",
                                boxShadow: 'none',
                            }}
                        >
                            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <BusinessIcon sx={{ fontSize: { xs: 12, sm: 13, md: 14 }, color: "#626B77" }} />
                                    <Typography
                                        sx={{
                                            fontSize: "14px",
                                            fontWeight: 400,
                                            color: "#626B77",
                                        }}
                                    >
                                        Company
                                    </Typography>
                                </Box>
                                <Box sx={{ mt: 1, ml: 2.25 }}>
                                    <Typography
                                        sx={{
                                            fontSize: "18px !important",
                                            fontWeight: 500,
                                            color: "#000",
                                        }}
                                    >
                                        {policy.policyCompany || '-'}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <ListAltIcon sx={{ fontSize: { xs: 12, sm: 13, md: 14 }, color: "#626B77" }} />
                                    <Typography
                                        sx={{
                                            fontSize: "14px",
                                            fontWeight: 400,
                                            color: "#626B77",
                                        }}
                                    >
                                        Policy No
                                    </Typography>
                                </Box>
                                <Box sx={{ mt: 1, ml: 2.25 }}>
                                    <Typography
                                        sx={{
                                            fontSize: "18px !important",
                                            fontWeight: 500,
                                            color: "#000",
                                        }}
                                    >
                                        {policy.policyNumber || '-'}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <DescriptionIcon sx={{ fontSize: { xs: 12, sm: 13, md: 14 }, color: "#626B77" }} />
                                    <Typography
                                        sx={{
                                            fontSize: "14px",
                                            fontWeight: 400,
                                            color: "#626B77",
                                        }}
                                    >
                                        Policy Type
                                    </Typography>
                                </Box>
                                <Box sx={{ mt: 1, ml: 2.25 }}>
                                    <Typography
                                        sx={{
                                            fontSize: "18px !important",
                                            fontWeight: 500,
                                            color: "#000",
                                        }}
                                    >
                                        {policy.policyType || '-'}
                                    </Typography>
                                </Box>
                            </Box>




                            {policy?.policyCategory !== 'motor' && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <TagIcon sx={{ fontSize: { xs: 12, sm: 13, md: 14 }, color: "#626B77" }} />
                                        <Typography
                                            sx={{
                                                fontSize: "14px",
                                                fontWeight: 400,
                                                color: "#626B77",
                                            }}
                                        >
                                            Sum Insured
                                        </Typography>
                                    </Box>
                                    <Box sx={{ mt: 1, ml: 2.25 }}>
                                        <Typography
                                            sx={{
                                                fontSize: "18px !important",
                                                fontWeight: 500,
                                                color: "#000",
                                            }}
                                        >
                                            {policy.sumInsured || '-'}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* Payment Details Section */}
                    <Box>
                        <Box sx={{ pt: 1, pb: 0.5, pl: 2 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontSize: '18px',
                                    fontWeight: 600,
                                }}
                            >
                                Payment Details
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                background: "#fff",
                                borderTop: '1px solid #E5E7EB',
                                padding: "12px 20px",
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
                                rowGap: "16px",
                                columnGap: "16px",
                                alignItems: "start",
                                boxShadow: 'none',
                            }}
                        >
                            {policy?.policyCategory === "motor" && (
                                <>
                                    {policy.policyType !== "Liability" && (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <CurrencyRupeeIcon sx={{ fontSize: { xs: 12, sm: 13, md: 14 }, color: "#626B77" }} />
                                                <Typography
                                                    sx={{
                                                        fontSize: "14px",
                                                        fontWeight: 400,
                                                        color: "#626B77",
                                                    }}
                                                >
                                                    OD Premium
                                                </Typography>
                                            </Box>
                                            <Box sx={{ mt: 1, ml: 2.25 }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: "18px !important",
                                                        fontWeight: 500,
                                                        color: "#000",
                                                    }}
                                                >
                                                    ₹ {parseFloat(policy.odPremium || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}

                                    {(policy.policyType === "Package" || policy.policyType === "Liability") && (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <CurrencyRupeeIcon sx={{ fontSize: { xs: 12, sm: 13, md: 14 }, color: "#626B77" }} />
                                                <Typography
                                                    sx={{
                                                        fontSize: "14px",
                                                        fontWeight: 400,
                                                        color: "#626B77",
                                                    }}
                                                >
                                                    TP Premium
                                                </Typography>
                                            </Box>
                                            <Box sx={{ mt: 1, ml: 2.25 }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: "18px !important",
                                                        fontWeight: 500,
                                                        color: "#000",
                                                    }}
                                                >
                                                    ₹ {parseFloat(policy.tpPremium || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    )}
                                </>
                            )}

                            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CurrencyRupeeIcon sx={{ fontSize: { xs: 12, sm: 13, md: 14 }, color: "#626B77" }} />
                                    <Typography
                                        sx={{
                                            fontSize: "14px",
                                            fontWeight: 400,
                                            color: "#626B77",
                                        }}
                                    >
                                        Net Premium
                                    </Typography>
                                </Box>
                                <Box sx={{ mt: 1, ml: 2.25 }}>
                                    <Typography
                                        sx={{
                                            fontSize: "18px !important",
                                            fontWeight: 500,
                                            color: "#000",
                                        }}
                                    >
                                        ₹ {parseFloat(policy.netPremium || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CurrencyRupeeIcon sx={{ fontSize: { xs: 12, sm: 13, md: 14 }, color: "#626B77" }} />
                                    <Typography
                                        sx={{
                                            fontSize: "14px",
                                            fontWeight: 400,
                                            color: "#626B77",
                                        }}
                                    >
                                        Total Premium
                                    </Typography>
                                </Box>
                                <Box sx={{ mt: 1, ml: 2.25 }}>
                                    <Typography
                                        sx={{
                                            fontSize: "18px !important",
                                            fontWeight: 500,
                                            color: "#000",
                                        }}
                                    >
                                        ₹ {parseFloat(policy.premiumAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CurrencyRupeeIcon sx={{ fontSize: { xs: 12, sm: 13, md: 14 }, color: "#626B77" }} />
                                    <Typography
                                        sx={{
                                            fontSize: "14px",
                                            fontWeight: 400,
                                            color: "#626B77",
                                        }}
                                    >
                                        Customer Discount
                                    </Typography>
                                </Box>
                                <Box sx={{ mt: 1, ml: 2.25 }}>
                                    <Typography
                                        sx={{
                                            fontSize: "18px !important",
                                            fontWeight: 500,
                                            color: "#000",
                                        }}
                                    >
                                        ₹ {parseFloat(policy.discount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CurrencyRupeeIcon sx={{ fontSize: { xs: 12, sm: 13, md: 14 }, color: "#626B77" }} />
                                    <Typography
                                        sx={{
                                            fontSize: "14px",
                                            fontWeight: 400,
                                            color: "#626B77",
                                        }}
                                    >
                                        Amount Payable
                                    </Typography>
                                </Box>
                                <Box sx={{ mt: 1, ml: 2.25 }}>
                                    <Typography
                                        sx={{
                                            fontSize: "18px !important",
                                            fontWeight: 500,
                                            color: "#000",
                                        }}
                                    >
                                        ₹ {parseFloat(policy.amountPayable || (parseFloat(policy.premiumAmount) - (parseFloat(policy.discount) || 0))).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <SouthWestIcon sx={{ fontSize: { xs: 12, sm: 13, md: 14 }, color: "#626B77" }} />
                                    <Typography
                                        sx={{
                                            fontSize: "14px",
                                            fontWeight: 400,
                                            color: "#626B77",
                                        }}
                                    >
                                        Amount Received
                                    </Typography>
                                </Box>
                                <Box sx={{ mt: 1, ml: 2.25 }}>
                                    <Typography
                                        sx={{
                                            fontSize: "18px !important",
                                            fontWeight: 500,
                                            color: "#000",
                                        }}
                                    >
                                        ₹ {parseFloat(policy.paidAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <AccountBalanceWalletIcon sx={{ fontSize: { xs: 12, sm: 13, md: 14 }, color: "#626B77" }} />
                                    <Typography
                                        sx={{
                                            fontSize: "14px",
                                            fontWeight: 400,
                                            color: "#626B77",
                                        }}
                                    >
                                        Balance Amount
                                    </Typography>
                                </Box>
                                <Box sx={{ mt: 1, ml: 2.25 }}>
                                    <Typography
                                        sx={{
                                            fontSize: "18px !important",
                                            fontWeight: 500,
                                            color: "#E11D48",
                                        }}
                                    >
                                        ₹ {Math.max((Number(policy.amountPayable || (parseFloat(policy.premiumAmount) - (parseFloat(policy.discount) || 0))) || 0) - (Number(policy.paidAmount) || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <PaymentIcon sx={{ fontSize: { xs: 12, sm: 13, md: 14 }, color: "#626B77" }} />
                                    <Typography
                                        sx={{
                                            fontSize: "14px",
                                            fontWeight: 400,
                                            color: "#626B77",
                                        }}
                                    >
                                        Mode of Payment
                                    </Typography>
                                </Box>
                                <Box sx={{ mt: 1, ml: 2.25 }}>
                                    <Typography
                                        sx={{
                                            fontSize: "18px !important",
                                            fontWeight: 500,
                                            color: "#000",
                                        }}
                                    >
                                        {policy.modeOfPayment || '-'}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <ListAltIcon sx={{ fontSize: { xs: 12, sm: 13, md: 14 }, color: "#626B77" }} />
                                    <Typography
                                        sx={{
                                            fontSize: "14px",
                                            fontWeight: 400,
                                            color: "#626B77",
                                        }}
                                    >
                                        Remarks
                                    </Typography>
                                </Box>
                                <Box sx={{ mt: 1, ml: 2.25 }}>
                                    <Typography
                                        sx={{
                                            fontSize: "16px !important",
                                            fontWeight: 400,
                                            color: "#000",
                                            whiteSpace: 'pre-wrap'
                                        }}
                                    >
                                        {policy.remarks || '-'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* Commission Details Section */}
                    <Box>
                        <Box sx={{ pt: 1, pb: 0.5, pl: 2 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontSize: '18px',
                                    fontWeight: 600,
                                }}
                            >
                                Commission Details
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                background: "#fff",
                                borderTop: '1px solid #E5E7EB',
                                padding: "16px 20px",
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr 1fr",
                                rowGap: "24px",
                                columnGap: "16px",
                                alignItems: "start",
                                boxShadow: 'none',
                            }}
                        >
                            {policy.policyCategory === 'motor' ? (
                                <>
                                    {policy.policyType !== "Liability" && (
                                        <>
                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                <Typography sx={{ fontSize: "14px", fontWeight: 400, color: "#626B77", mb: 1 }}>OD Comm. Rate</Typography>
                                                <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#000" }}>{policy.odCommissionRate || 0}%</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                <Typography sx={{ fontSize: "14px", fontWeight: 400, color: "#626B77", mb: 1 }}>OD Commission</Typography>
                                                <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#000" }}>₹ {parseFloat(policy.odCommissionAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                                            </Box>
                                            <Box /> {/* 3rd Column Empty */}
                                        </>
                                    )}

                                    {(policy.policyType === "Package" || policy.policyType === "Liability") && (
                                        <>
                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                <Typography sx={{ fontSize: "14px", fontWeight: 400, color: "#626B77", mb: 1 }}>TP Comm. Rate</Typography>
                                                <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#000" }}>{policy.tpCommissionRate || 0}%</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                <Typography sx={{ fontSize: "14px", fontWeight: 400, color: "#626B77", mb: 1 }}>TP Commission</Typography>
                                                <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#000" }}>₹ {parseFloat(policy.tpCommissionAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                                            </Box>
                                            <Box /> {/* 3rd Column Empty */}
                                        </>
                                    )}

                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography sx={{ fontSize: "14px", fontWeight: 400, color: "#626B77", mb: 1 }}>Gross Comm</Typography>
                                        <Typography sx={{ fontSize: "18px", fontWeight: 500, color: "#000" }}>₹ {baseCommission.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography sx={{ fontSize: "14px", fontWeight: 400, color: "#626B77", mb: 1 }}>TDS</Typography>
                                        <Typography sx={{ fontSize: "18px", fontWeight: 500, color: "#000" }}>{tdsRate}%</Typography>
                                    </Box>
                                    <Box /> {/* 3rd Column Empty */}

                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography sx={{ fontSize: "14px", fontWeight: 400, color: "#626B77", mb: 1 }}>Total Commission</Typography>
                                        <Typography sx={{
                                            fontSize: "18px",
                                            fontWeight: 500,
                                            color: totalCommission >= 0 ? '#166534' : '#E11D48'
                                        }}>₹ {totalCommission.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                                    </Box>
                                    <Box /> {/* 2nd Column Empty */}
                                    <Box /> {/* 3rd Column Empty */}
                                </>
                            ) : (
                                <>
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography sx={{ fontSize: "14px", fontWeight: 400, color: "#626B77", mb: 1 }}>Commission Rate</Typography>
                                        <Typography sx={{ fontSize: "18px", fontWeight: 500, color: "#000" }}>{commissionRate}%</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography sx={{ fontSize: "14px", fontWeight: 400, color: "#626B77", mb: 1 }}>Gross Comm</Typography>
                                        <Typography sx={{ fontSize: "18px", fontWeight: 500, color: "#000" }}>₹ {baseCommission.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography sx={{ fontSize: "14px", fontWeight: 400, color: "#626B77", mb: 1 }}>TDS</Typography>
                                        <Typography sx={{ fontSize: "18px", fontWeight: 500, color: "#000" }}>{tdsRate}%</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <Typography sx={{ fontSize: "14px", fontWeight: 400, color: "#626B77", mb: 1 }}>Total Commission</Typography>
                                        <Typography sx={{
                                            fontSize: "18px",
                                            fontWeight: 500,
                                            color: totalCommission >= 0 ? '#166534' : '#E11D48'
                                        }}>₹ {totalCommission.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                                    </Box>
                                </>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Edit Button - Exact Positioning as PolicyDetail */}
            {
                !isMobile && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: { xs: 'center', md: 'flex-end' },
                            gap: 2,
                            width: '100%',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: 'center',
                            mt: 2,
                        }}
                    >
                        <Button
                            variant="contained"
                            onClick={() => setShowEditDialog(true)}
                            sx={{
                                width: { xs: '100%', sm: 160 },
                                height: 60,
                                borderRadius: '8px',
                                p: '16px 12px 16px 8px',
                                gap: '4px',
                                boxShadow: 0,
                                backgroundColor: '#0063F7 !important',
                                color: '#FFFFFF !important',
                                fontFamily: 'Roboto',
                                fontWeight: 400,
                                fontSize: '14px',
                                lineHeight: '24px',
                                letterSpacing: '0.01em',
                                textTransform: 'none',
                                '&:hover': {
                                    backgroundColor: '#0063F7 !important',
                                    color: '#FFFFFF !important',
                                    boxShadow: 10
                                }
                            }}
                        >
                            Edit
                        </Button>
                    </Box>
                )
            }

            <CommissionEditDialog
                open={showEditDialog}
                onClose={() => setShowEditDialog(false)}
                policy={policy}
                userId={userId}
                onUpdate={(data) => {
                    // Mark cache invalid to force fresh fetch
                    firebaseCache.invalidateAfterWrite('policydetails_write', userId);
                    fetchPolicy(); // Refresh data
                    // Show Green Banner Toast
                    toast.success("Policy successfully edited", {
                        containerId: "bottom-center",
                        position: "bottom-center",
                        autoClose: 2000,
                        hideProgressBar: true,
                        closeOnClick: false,
                        pauseOnHover: false,
                        draggable: false,
                        closeButton: false,
                        style: {
                            backgroundColor: '#DCFCE7',
                            color: '#FFFFFF',
                            borderRadius: '8px',
                            fontSize: '16px',
                            fontWeight: 500,
                            padding: '12px 16px',
                            minHeight: '44px',
                            alignItems: 'center',
                            gap: '2px',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            marginBottom: '75vh', // Position customization
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            width: 'auto',
                            maxWidth: 'none',
                            display: 'inline-flex',
                        },
                        icon: (
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                style={{ color: '#FFFFFF' }}
                            >
                                <path
                                    d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        ),
                    });
                }}
            />

        </Box >
    );
};

export default CommissionDetail;
